/**
 * Bereich *Dokumente* (Spec 09, „Oberfläche · Wo"). Anders als *Geld* keine
 * eigene Reiterleiste — „Alle" · „Ohne Zuordnung" · je Art stehen in der
 * Kontext-Sidebar (`DokumentenSidebar` in `router.tsx`, wie `GeldSidebar`/
 * `AufgabenSidebar`), die Ansicht selbst steht in der URL (`?view=`) — ein
 * geteilter Link zeigt beim Empfänger dieselbe Liste (AK-515).
 *
 * `q` filtert zusätzlich zur Ansicht (Titel, Absender, Dateiname **und
 * Inhalt** — Spec 09, „Warum `?q=` jetzt den Inhalt trifft"), ebenfalls in
 * der URL. Kein eigener `data-testid` in der Spec vorgesehen — `document-search`
 * folgt der Namensform bestehender Suchfelder (`search-input` in
 * `routes/Search.tsx`, `contract-tenant-search`); im Bericht als Ergänzung
 * genannt, damit der `test-author` denselben Namen verwendet.
 *
 * **Die acht Artwerte stehen hier als Bezeichner, nicht als Wort** — dieselbe
 * Art von client-eigenem Identifikator wie `GELD_TAB_IDS`/`AUFGABEN_VIEWS`
 * in `router.tsx`/`Aufgaben.tsx`: `value`, nicht `label`. Die deutsche
 * Beschriftung kommt für die Sidebar-Zeilen ausschließlich aus
 * `GET /documents/categories` (AK-513).
 */

import { useNavigate, useSearch } from '@tanstack/react-router'

import { DocumentList } from '@/features/documents/DocumentList'
import { SenderList } from '@/features/documents/SenderList'
import { useDocumentsList } from '@/features/documents/api'
import { FIELD_CLASS } from '@/components/FormField'

export const DOCUMENT_CATEGORY_VALUES = [
  'invoice',
  'contract',
  'authority',
  'statement',
  'notice',
  'protocol',
  'correspondence',
  'other',
] as const

// Spec 24c, Oberfläche: `senders` ist eine **Ansicht** im Bereich
// *Dokumente*, kein Bereich, kein Reiter — dieselbe Bauform wie „unfiled"/
// jede Art, nur ohne eigenen Zähl-Endpunkt (`dokumente-view-senders` bleibt
// ohne Zahl, `router.tsx::DokumenteSidebar`).
const DOKUMENTE_VIEWS = ['all', 'unfiled', 'senders', ...DOCUMENT_CATEGORY_VALUES] as const
export type DokumenteView = (typeof DOKUMENTE_VIEWS)[number]

/** Ein unbekannter Wert landet auf „Alle" — dieselbe Toleranz wie
 * `toGeldTabId`. */
export function toDokumenteView(value: unknown): DokumenteView {
  return DOKUMENTE_VIEWS.includes(value as DokumenteView) ? (value as DokumenteView) : 'all'
}

type DocumentCategoryValue = (typeof DOCUMENT_CATEGORY_VALUES)[number]

/** Engt einen beliebigen String auf die acht bekannten Artwerte ein — für
 * `DokumenteView` selbst (immer schon einer der bekannten Bezeichner) **und**
 * für `category.value` aus `GET /documents/categories` (`router.tsx`,
 * `DokumenteSidebar`): Die Serverantwort ist dort `string` getippt, die
 * Route verlangt `DokumenteView`. */
export function isCategoryView(view: string): view is DocumentCategoryValue {
  return (DOCUMENT_CATEGORY_VALUES as readonly string[]).includes(view)
}

function emptyCopy(view: DokumenteView): { readonly title: string; readonly hint: string } {
  if (view === 'unfiled') {
    return { title: 'Der Eingang ist leer.', hint: 'Was du scannst und noch nicht einordnen kannst, landet hier.' }
  }
  if (isCategoryView(view)) {
    return { title: 'Keine Dokumente dieser Art.', hint: 'Die Art lässt sich an jedem Dokument nachtragen.' }
  }
  return { title: 'Noch keine Dokumente abgelegt.', hint: 'Dokument ablegen.' }
}

export function Dokumente() {
  const search = useSearch({ from: '/dokumente' })
  const navigate = useNavigate({ from: '/dokumente' })
  const isSenders = search.view === 'senders'

  // Der Hook läuft immer (Rules of Hooks) — `enabled` schaltet die Abfrage
  // ab, solange die Ansicht „Absender" zeigt, statt sie ungenutzt laufen zu
  // lassen.
  const query = useDocumentsList(
    {
      ...(search.view === 'unfiled' ? { unfiled: true } : {}),
      ...(isCategoryView(search.view) ? { category: search.view } : {}),
      ...(search.q !== '' ? { q: search.q } : {}),
    },
    { enabled: !isSenders },
  )

  if (isSenders) {
    return (
      <section aria-labelledby="senders-heading" data-testid="screen-documents" className="flex h-full flex-col">
        <SenderList />
      </section>
    )
  }

  const empty = emptyCopy(search.view)

  return (
    <section aria-labelledby="dokumente-heading" data-testid="screen-documents" className="flex h-full flex-col">
      <header className="border-border flex min-h-(--row-h) flex-wrap items-center justify-between gap-2 border-b px-(--page-x) py-2">
        <h1 id="dokumente-heading" className="text-page font-(--font-weight-page)">
          Dokumente
        </h1>
        <label htmlFor="document-search" className="sr-only">
          Dokumente durchsuchen
        </label>
        <input
          id="document-search"
          type="search"
          data-testid="document-search"
          placeholder="Titel, Absender oder Inhalt"
          value={search.q}
          onChange={(event) => {
            void navigate({ search: { ...search, q: event.target.value }, replace: true })
          }}
          className={`w-full max-w-64 text-text ${FIELD_CLASS}`}
        />
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <DocumentList query={query} emptyTitle={empty.title} emptyHint={empty.hint} isSearching={search.q !== ''} />
      </div>
    </section>
  )
}
